import React, { useState, useEffect, useRef } from "react";
import BASE_URL from "../../../../assets/global/url";
import { format } from "date-fns";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import { formatCurrency } from "../../../../utils/numberFormatter";

function PurchaseTab({ vendorId }) {
  const [purchaseData, setPurchaseData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);

  // Initialize server pagination - now backed by purchase_report_route.js
  // (PayableJournal ledger, same source as the SOA export) instead of payable_route.js
  const pagination = useServerPagination(
    `${BASE_URL}/purchase_report/purchase-tab`,
    10,
    {
      id: vendorId,
    },
  );

  // Search pagination hook
  const searchPagination = useServerPagination(
    `${BASE_URL}/purchase_report/purchase-tab-search`,
    10,
    {
      id: vendorId,
      searchText: searchTerm,
    },
  );

  // Transform pagination data to display format
  useEffect(() => {
    const data = isSearching ? searchPagination.data : pagination.data;

    if (data && data.isFetch && data.isFetch.length > 0) {
      const transformedData = data.isFetch.map((item) => ({
        transaction_date: item.transaction_date,
        transaction_id: item.transaction_id,
        charge: item.charge || 0,
        purchase_date: item.purchase_date,
        date_approved: item.date_approved,
        total_amount: item.total_amount || 0,
        currency: item.currency || "PHP",
        bank: item.bank,
        checkNo: item.checkNo,
        checkDate: item.checkDate,
        credit: item.credit || 0,
        balance: item.balance || 0,
      }));
      setPurchaseData(transformedData);
    } else {
      setPurchaseData([]);
    }
  }, [pagination.data, searchPagination.data, isSearching]);

  // Debounced search handler
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      if (value.trim() !== "") {
        setIsSearching(true);
        searchPagination.updateParams({
          id: vendorId,
          searchText: value,
        });
      } else {
        // Auto clear when search term is empty
        setIsSearching(false);
        pagination.updateParams({
          id: vendorId,
        });
      }
    }, 500); // 500ms debounce delay
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const isLoading = isSearching ? searchPagination.loading : pagination.loading;
  const currentPagination = isSearching ? searchPagination : pagination;

  return (
    <div>
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by Transaction Number or Date..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light">
            <tr>
              <th className="p-2">Transaction Date</th>
              <th className="p-2">Transaction Number</th>
              <th className="p-2">Charge</th>
              <th className="p-2">Purchase Date</th>
              <th className="p-2">Date Approved</th>
              <th className="p-2">Bank</th>
              <th className="p-2">Check #</th>
              <th className="p-2">Check Date</th>
              <th className="p-2">Credit</th>
              <th className="p-2">Balance</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={10}>
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : purchaseData.length > 0 ? (
              purchaseData.map((data, index) => (
                <tr key={index}>
                  <td className="p-2">
                    {data.transaction_date
                      ? format(new Date(data.transaction_date), "MMM/dd/yyyy")
                      : "N/A"}
                  </td>
                  <td className="p-2">{data.transaction_id}</td>
                  <td className="p-2">
                    {data.charge ? formatCurrency(data.charge, "PHP") : "0.00"}
                  </td>
                  <td className="p-2">
                    {data.purchase_date
                      ? format(new Date(data.purchase_date), "MMM/dd/yyyy")
                      : "N/A"}
                  </td>
                  <td className="p-2">
                    {data.date_approved
                      ? format(new Date(data.date_approved), "MMM/dd/yyyy")
                      : "N/A"}
                  </td>
                  <td className="p-2">{data.bank || "--"}</td>
                  <td className="p-2">{data.checkNo || "--"}</td>
                  <td className="p-2">
                    {data.checkDate && data.checkDate !== "--"
                      ? format(new Date(data.checkDate), "MMM/dd/yyyy")
                      : "--"}
                  </td>
                  <td className="p-2">
                    {data.credit ? formatCurrency(data.credit, "PHP") : "0.00"}
                  </td>
                  <td className="p-2">{formatCurrency(data.balance, "PHP")}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="text-center text-muted p-4">
                  <em>
                    {isSearching && searchTerm.trim() !== ""
                      ? "No results found"
                      : "No purchase data available"}
                  </em>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <PaginationControls {...currentPagination} />
    </div>
  );
}

export default PurchaseTab;
